import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { RequestsNavButtonMenu } from "./RequestsNavButtonMenu";

import { CLOSE_REVIEW_LOGS_DRAWER_EVENT } from "@/components/SidebarSearchLayout/Sidebar/SecondarySidebar/SearchBar/ReviewLogsDrawerControls/constants";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { SESSION_STORAGE } from "@/constants";
import { useNavigateWithSearch } from "@/hooks";
import { preloadAnalyticsPage } from "@/routes/analytics/loadAnalyticsPage";
import { stripReviewLogsDrawerIntent } from "@/utils/requestLogUrlParams";
import {
  isWorkspaceAnalyticsPathname,
  isWorkspaceRequestLogsTablePathname,
} from "@/utils/workspacePathMatchers";

interface RequestsNavButtonProps {
  isCollapsed?: boolean;
  isObservabilityOpen?: boolean;
  onToggleObservability?: () => void;
  liveLogsTooltip?: string;
  showObservabilityCaret?: boolean;
}

export const RequestsNavButton = ({
  isCollapsed = false,
  isObservabilityOpen = false,
  onToggleObservability,
  liveLogsTooltip = "Requests",
  showObservabilityCaret = true,
}: RequestsNavButtonProps) => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigateWithSearch();
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openSubmenu = useCallback(() => {
    clearCloseTimer();
    setSubmenuOpen(true);
  }, [clearCloseTimer]);

  const scheduleCloseSubmenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setSubmenuOpen(false), 150);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const analyticsPath = workspaceId ? `/workspace/${workspaceId}/analytics` : null;
  const isAnalyticsActive = isWorkspaceAnalyticsPathname(location.pathname);
  const isRequestLogsPageActive = isWorkspaceRequestLogsTablePathname(location.pathname);
  const isOnRequestsAnalyticsPage = isAnalyticsActive || isRequestLogsPageActive;
  const isRequestLogsDrawerActive =
    Boolean(isObservabilityOpen) && !isAnalyticsActive && !isRequestLogsPageActive;
  const isRequestsSectionActive =
    isRequestLogsPageActive || isAnalyticsActive || Boolean(isObservabilityOpen);

  useEffect(() => {
    if (!workspaceId) {
      return;
    }
    preloadAnalyticsPage();
  }, [workspaceId]);

  const storeReturnUrlIfNeeded = useCallback(() => {
    if (isOnRequestsAnalyticsPage) {
      return;
    }
    sessionStorage.setItem(
      SESSION_STORAGE.REQUESTS_ANALYTICS_RETURN_URL,
      `${location.pathname}${location.search}${location.hash}`,
    );
  }, [isOnRequestsAnalyticsPage, location.hash, location.pathname, location.search]);

  const handleAnalyticsClick = useCallback(() => {
    if (!analyticsPath || isAnalyticsActive) {
      setSubmenuOpen(false);
      return;
    }
    storeReturnUrlIfNeeded();
    preloadAnalyticsPage();
    window.dispatchEvent(new Event(CLOSE_REVIEW_LOGS_DRAWER_EVENT));
    navigate(analyticsPath, { search: stripReviewLogsDrawerIntent(location.search) });
    setSubmenuOpen(false);
  }, [analyticsPath, isAnalyticsActive, location.search, navigate, storeReturnUrlIfNeeded]);

  const handleRequestLogsClick = useCallback(() => {
    if (!workspaceId || isRequestLogsPageActive) {
      setSubmenuOpen(false);
      return;
    }
    storeReturnUrlIfNeeded();
    preloadAnalyticsPage();
    window.dispatchEvent(new Event(CLOSE_REVIEW_LOGS_DRAWER_EVENT));
    navigate(`/workspace/${workspaceId}/request-logs`, {
      search: stripReviewLogsDrawerIntent(location.search),
    });
    setSubmenuOpen(false);
  }, [isRequestLogsPageActive, location.search, navigate, storeReturnUrlIfNeeded, workspaceId]);

  const buttonVariant = isRequestsSectionActive ? "blue" : "ghost";
  const caretRotationClass = isRequestLogsDrawerActive
    ? "rotate-180 text-blue-600"
    : "text-gray-400";

  const handlePrimaryClick = useCallback(() => {
    if (isOnRequestsAnalyticsPage) {
      return;
    }
    onToggleObservability?.();
  }, [isOnRequestsAnalyticsPage, onToggleObservability]);

  return (
    <BasicTooltip shouldShow={isCollapsed} content={liveLogsTooltip} side="top">
      <span>
        <RequestsNavButtonMenu
          isCollapsed={isCollapsed}
          submenuOpen={submenuOpen}
          onSubmenuOpenChange={setSubmenuOpen}
          onSubmenuOpen={openSubmenu}
          onSubmenuClose={scheduleCloseSubmenu}
          buttonVariant={buttonVariant}
          caretRotationClass={caretRotationClass}
          isRequestsSectionActive={isRequestsSectionActive}
          showObservabilityCaret={showObservabilityCaret}
          isRequestLogsPageActive={isRequestLogsPageActive}
          isAnalyticsActive={isAnalyticsActive}
          onPrimaryClick={handlePrimaryClick}
          onRequestLogsClick={handleRequestLogsClick}
          onAnalyticsClick={handleAnalyticsClick}
        />
      </span>
    </BasicTooltip>
  );
};
